import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import axios from "axios";
import Icon from '@mdi/react';
import { mdiCalendarPlus } from '@mdi/js';
import AddressAutocomplete from './AddressAutocomplete';

function AddEventModal({ show, handleClose, onEventAdded, seasonId }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "other",
    league: "",
    season: seasonId || "",
    team: "",
    sponsor: "",
    location_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    latitude: null,
    longitude: null,
    datetime: "",
    is_public: true,
  });

  const [seasonDetails, setSeasonDetails] = useState(null);
  const [teams, setTeams] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  // Memoize the callback functions to prevent AddressAutocomplete re-initialization
  const handleAddressChange = useCallback((value) => {
    setFormData(prev => {
      const newData = { ...prev, address_line1: value };
      return newData;
    });
  }, []);

  const handleAddressSelect = useCallback((addressData) => {
    const locationName = addressData.displayName || addressData.formattedAddress || '';

    setFormData((prev) => ({
      ...prev,
      location_name: locationName,
      address_line1: addressData.address_line1,
      address_line2: addressData.address_line2,
      city: addressData.city,
      state: addressData.state,
      zip_code: addressData.zip_code,
      country: addressData.country,
      latitude: addressData.latitude,
      longitude: addressData.longitude,
    }));
  }, []);

  const fetchSeasonDetails = async () => {
    try {
      const response = await axios.get(`/api/leagues/seasons/${seasonId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.status === 200) {
        setSeasonDetails(response.data);
        // Set the league from the season details
        setFormData(prev => ({
          ...prev,
          league: response.data.league
        }));
      }
    } catch (error) {
      console.error('Error fetching season details:', error);
    }
  };

  const fetchTeams = async (seasonId) => {
    setTeamsLoading(true);
    try {
      const response = await axios.get(`/api/teams/${seasonId}/teams/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.status === 200) {
        setTeams(response.data);
      }
    } catch (error) {
      // Error fetching teams
    } finally {
      setTeamsLoading(false);
    }
  };

  const fetchSponsors = async (seasonId) => {
    setSponsorsLoading(true);
    try {
      const response = await axios.get(`/api/leagues/sponsors/?season_id=${seasonId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (response.status === 200) {
        setSponsors(response.data);
      }
    } catch (error) {
      // Error fetching sponsors
    } finally {
      setSponsorsLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !seasonId) return;
    fetchSeasonDetails();
  }, [show, seasonId]);

  useEffect(() => {
    if (seasonId) {
      fetchTeams(seasonId);
      fetchSponsors(seasonId);
    }
  }, [seasonId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessages([]);

    // Validate required fields
    const requiredFields = {
      title: 'Event Title',
      event_type: 'Event Type',
      datetime: 'Date & Time',
      address_line1: 'Event Location'
    };

    // Skip title validation for post_game events
    if (formData.event_type === 'post_game') {
      delete requiredFields.title;
    }

    const missingFields = [];
    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        missingFields.push(label);
      }
    }

    if (missingFields.length > 0) {
      setErrorMessages([`Please fill in the following required fields: ${missingFields.join(', ')}`]);
      setLoading(false);
      return;
    }

    // Format datetime for API
    const eventData = {
      ...formData,
      datetime: formData.datetime ? new Date(formData.datetime).toISOString() : null,
    };

    // Handle empty sponsor value
    if (!eventData.sponsor || eventData.sponsor === "") {
      eventData.sponsor = null;
    }

    // For post-game events, send a placeholder title that will be replaced by the backend
    if (formData.event_type === 'post_game') {
      eventData.title = 'Post-Game Social - Title will be auto-generated';
    }

    axios
      .post("/api/leagues/events/", eventData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        onEventAdded(response.data);
        setFormData({
          title: "",
          description: "",
          event_type: "other",
          league: "",
          season: seasonId || "",
          team: "",
          sponsor: "",
          location_name: "",
          address_line1: "",
          address_line2: "",
          city: "",
          state: "",
          zip_code: "",
          country: "USA",
          latitude: null,
          longitude: null,
          datetime: "",
          is_public: true,
        });
        handleClose();
      })
      .catch((error) => {
        const data = error.response?.data || {};

        if (typeof data === 'object') {
          const parts = Object.entries(data).flatMap(([field, msgs]) =>
            (Array.isArray(msgs) ? msgs : [msgs]).map(m =>
              `Field [${field}]: ${m}`
            )
          );
          setErrorMessages(parts);
        } else {
          setErrorMessages(["An unexpected error occurred. Please try again."]);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Icon path={mdiCalendarPlus} size={1} className="me-2" />
          Add Event
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessages.map((err, i) => (
          <div key={i} className="text-danger mb-2">{err}</div>
        ))}
        <Form onSubmit={handleSubmit} noValidate>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Type *</Form.Label>
                <Form.Select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                >
                  <option value="other">Other</option>
                  <option value="post_game">Post-Game Social</option>
                  <option value="team_meet">Team Meeting</option>
                  <option value="info_meet">Info Session</option>
                  <option value="league_party">League Party</option>
                  <option value="tournament">Tournament</option>
                  <option value="practice">Practice</option>
                  <option value="fundraiser">Fundraiser</option>
                  <option value="volunteer">Volunteer Event</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder={formData.event_type === 'post_game' ? 'Title will be auto-generated' : 'Enter event title'}
                  required={formData.event_type !== 'post_game'}
                  readOnly={formData.event_type === 'post_game'}
                  className={formData.event_type === 'post_game' ? 'bg-light' : ''}
                />
                {formData.event_type === 'post_game' && (
                  <Form.Text className="text-muted">
                    Title will be automatically generated based on the date and location.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter event description"
                />
              </Form.Group>
            </Col>
          </Row>



          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Team (Optional)</Form.Label>
                <Form.Select
                  name="team"
                  value={formData.team}
                  onChange={handleChange}
                  disabled={teamsLoading}
                >
                  <option value="">All Teams</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Select>
                {teamsLoading && <Spinner animation="border" size="sm" className="mt-1" />}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Sponsor (Optional)</Form.Label>
                <Form.Select
                  name="sponsor"
                  value={formData.sponsor}
                  onChange={handleChange}
                  disabled={sponsorsLoading}
                >
                  <option value="">No Sponsor</option>
                  {sponsors.map((sponsor) => (
                    <option key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </option>
                  ))}
                </Form.Select>
                {sponsorsLoading && <Spinner animation="border" size="sm" className="mt-1" />}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Date & Time *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="datetime"
                  value={formData.datetime}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Public Event</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleChange}
                    label="Make this event public"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Event Location *</Form.Label>
                <AddressAutocomplete
                  value={formData.address_line1}
                  onChange={handleAddressChange}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Search for event location..."
                  showPreview={true}
                />
                <Form.Text className="text-muted">
                  Search for an address and select it from the dropdown. The location name and address details will be parsed when you save the event.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>


        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            'Create Event'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddEventModal; 